import { useState, useEffect, useCallback } from "react";

import useSSE from "../../../utils/useSSE";
import strings from "../../../locales/ro.json";
import { useUI } from "../../../hooks/usePermission";
import { useApi } from "../../../hooks/useApi";

const t = strings.attendance;
const ACCURACY_THRESHOLD_METERS = 50;
const ALLOWED_GROUPS = [strings.basicUserGroups.juniors, strings.basicUserGroups.seniors];

export function useAttendanceLogic(user) {
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(false);
  const [attendanceId, setAttendanceId] = useState(null);
  const [activeIsOnline, setActiveIsOnline] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const canManageQueue = useUI('action.attendance.queue');
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [retryPrompt, setRetryPrompt] = useState(false);
  const [choicePrompt, setChoicePrompt] = useState(false);
  const [onlinePrompt, setOnlinePrompt] = useState(false);
  const [meetingTypeSelectorOpen, setMeetingTypeSelectorOpen] = useState(false);
  const [isOnlineMode, setIsOnlineMode] = useState(false);
  const [error, setError] = useState(null);

  const { api, authToken } = useApi();

  const showPopup = (message) => {
    setQueueOpen(false);
    setPopup(message);
  };
  const closePopup = () => setPopup(null);

  const closeSelectorModal = () => {
    setMeetingTypeSelectorOpen(false);
    setError(null);
    setIsOnlineMode(false);
  };

  const locateUser = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (pos.coords.accuracy > ACCURACY_THRESHOLD_METERS) {
            resolve({ tooInaccurate: true });
          } else {
            resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
          }
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const fetchStatus = useCallback(async () => {
    if (!user?.profile?.id || !authToken) return;
    try {
      const res = await api(`attendance/attending/${user.profile.id}`);
      setStatus(res.status === "notAttending" ? null : res.status);
    } catch (err) {
      console.error("Error fetching attendance status:", err);
    }
  }, [api, authToken, user?.profile?.id]);

  const fetchQueue = useCallback(async () => {
    if (!canManageQueue) return;
    try {
      const res = await api("attendance/waitlist");
      if (Array.isArray(res)) setWaitlist(res);
    } catch (err) {
      console.error("Error fetching waitlist:", err);
    }
  }, [api, canManageQueue]);

  const addToQueue = async () => {
    if (!authToken) return;
    try {
      await api("attendance/request-manual", "POST");
      setStatus("waiting");
    } catch (err) {
      console.error("Error adding to queue:", err);
    }
  };

  const handleQueueAdd = async (successMessage) => {
    setLoading(true);
    try {
      await addToQueue();
      showPopup(successMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateWithLocation = async ({ addToQueueOnFail }) => {
    setLoading(true);
    try {
      const location = await locateUser();

      if (!location || location.tooInaccurate) {
        const msg = location?.tooInaccurate ? t.locationTooInaccurate : t.geolocationDenied;
        if (addToQueueOnFail) {
          showPopup(msg);
          await addToQueue();
        } else {
          showPopup(location?.tooInaccurate ? t.locationTooInaccurate : t.retryNoLocation);
        }
        return;
      }

      try {
        const res = await api("attendance/validate", "POST", {
          latitude: location.latitude,
          longitude: location.longitude,
        });

        if (res.message === "Location validated successfully!") {
          showPopup(t.locationSuccess);
          setStatus("attending");
        } else {
          if (addToQueueOnFail) {
            showPopup(t.locationFailed);
            await addToQueue();
          } else {
            showPopup(t.retryFailed);
          }
        }
      } catch (err) {
        const isTooFar = err.message?.includes("400");
        if (addToQueueOnFail) {
          showPopup(isTooFar ? t.tooFar : t.locationFailed);
          await addToQueue();
        } else {
          showPopup(isTooFar ? t.tooFar : t.retryFailed);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAttend = async () => {
    if (loading) return;

    const statute = user.profile.groups;
    if (statute && !ALLOWED_GROUPS.some((group) => statute.includes(group))) {
      return showPopup(t.statuteError);
    }

    if (!active) return showPopup(t.noActiveSession);
    if (status === "attending") return showPopup(t.alreadyPresent);

    if (status === "waiting") {
      if (activeIsOnline) return showPopup(t.waitingOnlineInfo);
      return setRetryPrompt(true);
    }

    if (activeIsOnline) {
      setOnlinePrompt(true);
    } else {
      setChoicePrompt(true);
    }
  };

  const startAttendance = async (meetingType, isOnline) => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      let location = null;
      if (!isOnline) {
        location = await locateUser();
        if (!location || location.tooInaccurate) {
          setError(location?.tooInaccurate ? t.locationTooInaccurate : t.geolocationRequired);
          return;
        }
      } else {
        location = { latitude: 0, longitude: 0 };
      }

      const res = await api("attendance/open", "POST", {
        meeting_type: meetingType,
        longitude: location.longitude,
        latitude: location.latitude,
        is_online: isOnline,
      });

      setMeetingTypeSelectorOpen(false);
      if (res.message === "Attendance opened successfully" || res.data) {
        setActive(true);
        setAttendanceId(res.data?.id ?? null);
        setActiveIsOnline(isOnline);
        showPopup(t.openedSuccess);
      } else {
        showPopup(t.alreadyActive);
      }
    } catch (err) {
      console.error("Error starting attendance:", err);
      setError(err.message || t.openError);
    } finally {
      setLoading(false);
      setIsOnlineMode(false);
    }
  };

  const closeAttendance = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const id = attendanceId;
      if (!id) return showPopup(t.noActiveToClose);

      const res = await api(`attendance/close/${id}`, "POST");
      if (res) {
        setAttendanceId(null);
        showPopup(t.closedSuccess);
        setQueueOpen(false);
        await fetchQueue();
      } else {
        showPopup(t.closeError);
      }
    } catch (err) {
      console.error("Error closing attendance:", err);
      showPopup(t.closeError);
    } finally {
      setLoading(false);
    }
  };

  const toggleAttendance = () => (active ? closeAttendance() : setMeetingTypeSelectorOpen(true));

  const showQueue = async () => {
    if (!active) return showPopup(t.noActiveSession);
    try {
      const res = await api("attendance/waitlist");
      if (Array.isArray(res)) setWaitlist(res);
      setQueueOpen(true);
    } catch (err) {
      showPopup(t.queueError);
    }
  };

  const handleValidation = async (id, command) => {
    try {
      await api(`attendance/${command}/${id}`, "POST");
      setWaitlist((prev) => prev.filter((u) => (u.id || u.profile?.id) !== id));
    } catch (err) {
      console.error(`Error during ${command}:`, err);
      showPopup(t.validationError);
    }
  };

  useEffect(() => {
    if (!authToken) return;

    const init = async () => {
      try {
        const res = await api("attendance/active");
        setActive(res.active);
        if (res.active) {
          setAttendanceId(res.attendanceId ?? null);
          setActiveIsOnline(res.isOnline ?? false);
        } else {
          setAttendanceId(null);
          setActiveIsOnline(false);
        }
      } catch (err) {
        console.error("Failed to fetch attendance state:", err);
      }
    };

    init();
    fetchStatus();
    fetchQueue();
  }, [authToken, api, fetchStatus, fetchQueue]);

  useSSE(
    "stream/global",
    user?.accessToken,
    (msg) => {
      if (msg.type === "attendance_state_changed") {
        const isActive = msg.data.is_active;
        setActive(isActive);
        if (isActive) {
          setAttendanceId(msg.data.id ?? null);
          setActiveIsOnline(msg.data.is_online ?? false);
        } else {
          setAttendanceId(null);
          setActiveIsOnline(false);
          setStatus(null);
          setQueueOpen(false);
        }
        fetchStatus();
      } else if (msg.type === "attendance_approved" && msg.userId == user.profile.id) {
        showPopup(t.adminApproved);
        setStatus("attending");
      } else if (msg.type === "waitlist_update") {
        fetchQueue();
      }
    },
    (err, isVisible) => {
      if (isVisible) console.error("SSE Connection Error in Attendance:", err);
    }
  );

  return {
    status,
    active,
    waitlist,
    loading,
    popup,
    queueOpen,
    setQueueOpen,
    retryPrompt,
    setRetryPrompt,
    choicePrompt,
    setChoicePrompt,
    onlinePrompt,
    setOnlinePrompt,
    meetingTypeSelectorOpen,
    isOnlineMode,
    setIsOnlineMode,
    error,
    closePopup,
    closeSelectorModal,
    handleQueueAdd,
    validateWithLocation,
    handleAttend,
    startAttendance,
    toggleAttendance,
    showQueue,
    handleValidation,
    t
  };
}
